import { useMemo, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { MAIL_PLACEHOLDERS, mailFieldsFor, mailSampleValues, mailTextLabel, unsupportedPlaceholders } from "@talentkontor/domain";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { Dialog, Money, Toolbar, matchesSearch, useAction } from "../../../components/ui";
import { date, money, today as todayIn } from "../../../lib/format";
import {
  deleteArticle, loadVorlagen, resetOrgText, saveArticle, saveOrgText, saveStatusLabel,
  type ArticleDbRow, type KindRow, type PlatformTextRow, type StatusLabelRow, type TemplateRow, type VorlagenData,
} from "./data";
import {
  ARTICLE_CATEGORIES, ARTICLE_UNITS, CURRENCIES, isHiddenText, LANGUAGES, LEGACY_TEXT_KEYS, STATUS_KEYS, TEXT_GROUPS, centsToText, droppedPlaceholders, emptyArticle,
  invoiceNumberExample, parsePgArray, renderPreview, stageLabel, textGroup, textLabel as textLabelBase, textRank, unknownPlaceholders, validateArticle, type ArticleDraft,
} from "./model";
import { AreaField, NurAdmin, CheckField, Confirm, FormErrors, SelectField, SettingsFrame, TextField } from "./parts";
import { betragHinweis } from "../../../components/Erkannt";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import type { SenderDomainRecord } from "../../../data/api";
import { AbsenderdomainStand } from "./Absenderdomain";
import type { SenderDomainStatus } from "./absenderdomain-model";
import { ZwischenstaendePanel } from "./Zwischenstaende";
import { t, ta, nameIn, sprachName, istCreatorKonto } from "../../../lib/sprache";

/**
 * Einstellungen · Vorlagen (11_app_agentur/einstellungen-vorlagen.html): Vertragsvorlagen (Verweis auf den Bereich Verträge),
 * Mail- und Mahntexte der Agentur gegen die Texte der Plattform, Artikelkatalog für Rechnungen, Status und Begriffe.
 * 0056 (Befunde 17, 19): Absender und Antworten der Agentur (Antwortadresse je Zweck, Signatur, persönlicher Absender,
 * eigene Absenderdomain vorbereitet); Vorschau mit Beispielwerten für jeden Platzhalter, Platzhalter je Mailart, Warnung,
 * wenn eine eigene Fassung Werte nutzt, die diese Mail nie hat (dann gilt beim Versand der Standardtext).
 */

/** Bezeichnung aus den Einstellungen, für neuere Mailarten aus der Fachlogik (z. B. Bankverbindung geändert). */
const textLabel = (key: string): string => {
  const own = textLabelBase(key);
  return own !== key ? own : mailTextLabel(key);
};
const isMail = (key: string) => key.startsWith("email.") || key.startsWith("dunning.");
export function Vorlagen() {
  const ws = useStaffWorkspace();
  if (!ws.isChef) return <NurAdmin />;
  return <VorlagenSeite orgId={ws.org.org_id} orgName={ws.org.name} currency={ws.org.currency} timeZone={ws.org.timezone} />;
}

function VorlagenSeite({ orgId, orgName, currency, timeZone }: { orgId: string; orgName: string; currency: string; timeZone: string }) {
  const q = useQuery({ queryKey: ["org_texts", orgId, "vorlagen"], queryFn: () => loadVorlagen(api, orgId) });
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const d = q.data;
  return (
    <SettingsFrame section="vorlagen" orgId={orgId} title={t("Mailvorlagen", "Email templates")}
      text={t("Mail- und Mahntexte, Status und Begriffe. Absender und Antwortadressen stehen unter Mailversand, der Artikelkatalog unter Positionsarten & Artikel.", "Email and dunning texts, statuses and terms. Sender and reply addresses are under Email sending, the item catalogue under Item types & articles.")}>
      {/* IA-1: EIN Ort für Absender und Mailversand; Artikel bei den Positionsarten (keine Doppelung mehr) */}
      <p className="notice tk-verweise">
        <Link to="/agentur/einstellungen/mailversand">{t("Absender, Antwortadressen und Absenderdomain: Mailversand", "Sender, reply addresses and sender domain: Email sending")}</Link>
        {" · "}
        <Link to="/agentur/einstellungen/positionsarten">{t("Artikelkatalog: Positionsarten & Artikel", "Item catalogue: Item types & articles")}</Link>
        {" · "}
        <Link to="/agentur/einstellungen/pdf-vorlagen#vertrag">{t("Vertragsvorlagen: PDF-Vorlagen", "Contract templates: PDF templates")}</Link>
      </p>
      <TexteForm orgId={orgId} orgName={orgName} currency={currency} timeZone={timeZone} data={d} />
      <BegriffePanel key={d.labels.map((l) => `${l.status}${l.label_de}${l.label_en}${l.hidden}`).join()} orgId={orgId} labels={d.labels} />
      <ZwischenstaendePanel orgId={orgId} labels={d.labels} />
    </SettingsFrame>
  );
}

// ── Absender und Antworten (Befund 17) ──────────────────────────────────────────

interface MailSettingsRow {
  /** 0226: Band „Erstellt mit CreatorOffice“ in Mails ausgeblendet (wirkt nur mit dem Modul) */
  hide_product_badge?: boolean | null;
  reply_to_contracts: string | null;
  reply_to_invoices: string | null;
  reply_to_creators: string | null;
  /** Blindkopie jeder Rechnungs- und Mahnmail (0080) */
  invoice_copy_to?: string | null;
  personal_sender: boolean;
  signature: string | null;
  sender_domain: string | null;
  sender_local_part: string;
  sender_domain_status: SenderDomainStatus;
  /** 0102: DNS-Einträge des Versanddienstes, Zeitpunkt und Fehler der letzten Prüfung */
  sender_domain_records?: SenderDomainRecord[] | null;
  sender_domain_checked_at?: string | null;
  sender_domain_error?: string | null;
}
const MAIL_OK = /^[^@\s"<>]+@[^@\s"<>]+\.[^@\s"<>]+$/;
const DOMAIN_OK = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/;

/**
 * Wohin Antworten auf Mails gehen und womit die Agentur unterschreibt (org_mail_settings, 0056). Ohne Angabe gilt die
 * Antwortadresse der Agentur (Firma). Mit „persönlicher Absender“ tragen Mails, die eine Person auslöst, deren Adresse
 * und Signatur (jede Person pflegt sie unter Benachrichtigungen · Kanäle). Eigene Absenderdomain (0102): nach dem Speichern legt
 * die Serverfunktion sender-domain sie beim Versanddienst an; Stand und DNS-Einträge zeigt AbsenderdomainStand.
 */
export function AbsenderPanel({ orgId }: { orgId: string }) {
  const q = useQuery({
    queryKey: ["org_mail_settings", orgId, "vorlagen"],
    queryFn: async () => {
      const [rows, ready] = await Promise.all([
        api.select<MailSettingsRow>("org_mail_settings", {
          columns: "hide_product_badge, reply_to_contracts, reply_to_invoices, reply_to_creators, invoice_copy_to, personal_sender, signature, sender_domain, sender_local_part, sender_domain_status, sender_domain_records, sender_domain_checked_at, sender_domain_error",
          filters: [["org_id", "eq", orgId]],
        }),
        api.select<{ value: unknown }>("platform_defaults", { columns: "value", filters: [["key", "eq", "custom_sender_domains_ready"]] }).catch(() => []),
      ]);
      return { row: rows[0] ?? null, ready: ready[0]?.value === true || ready[0]?.value === "true" };
    },
  });
  if (!q.data) return null;
  return <AbsenderForm key={JSON.stringify(q.data.row)} orgId={orgId} row={q.data.row} ready={q.data.ready} />;
}

function AbsenderForm({ orgId, row, ready }: { orgId: string; row: MailSettingsRow | null; ready: boolean }) {
  const { busy, error, run } = useAction();
  // Tarifstaffel (0099): eigene Absenderdomain setzen oder ändern erst mit dem Modul; entfernen geht immer
  const domainLocked = useModul("module_sender_domain").gesperrt;
  // 0226: Hinweis „Erstellt mit CreatorOffice“ am Ende der Mails; ausblenden ab Tarif Agentur (im Test im vollen Umfang bedienbar)
  const badgeLocked = useModul("module_mail_unbranded").gesperrt;
  const [badgeAn, setBadgeAn] = useState(!(row?.hide_product_badge ?? false));
  const badgeSetzen = (an: boolean) => void run("badge", async () => {
    await api.rpc("org_mail_badge_set", { p_org: orgId, p_hidden: !an });
    setBadgeAn(an);
  }, an ? t("Der CreatorOffice-Hinweis steht wieder in euren Mails.", "The CreatorOffice note is shown in your emails again.") : t("Der CreatorOffice-Hinweis ist in euren Mails ausgeblendet.", "The CreatorOffice note is hidden in your emails."));
  const [d, setD] = useState({
    reply_to_contracts: row?.reply_to_contracts ?? "", reply_to_invoices: row?.reply_to_invoices ?? "", reply_to_creators: row?.reply_to_creators ?? "",
    invoice_copy_to: row?.invoice_copy_to ?? "",
    personal_sender: row?.personal_sender ?? false, signature: row?.signature ?? "", sender_domain: row?.sender_domain ?? "", sender_local_part: row?.sender_local_part ?? "post",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof typeof d>(k: K) => (v: (typeof d)[K]) => setD((x) => ({ ...x, [k]: v }));
  const status = row?.sender_domain_status ?? "none";
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    for (const k of ["reply_to_contracts", "reply_to_invoices", "reply_to_creators", "invoice_copy_to"] as const) {
      if (d[k].trim() && !MAIL_OK.test(d[k].trim())) errs[k] = t("Bitte eine gültige E-Mail-Adresse eingeben.", "Please enter a valid email address.");
    }
    if (d.sender_domain.trim() && !DOMAIN_OK.test(d.sender_domain.trim().toLowerCase())) errs.sender_domain = t("Bitte eine Domain wie agentur.de eingeben.", "Please enter a domain such as agentur.de.");
    if (domainLocked && d.sender_domain.trim() && d.sender_domain.trim().toLowerCase() !== (row?.sender_domain ?? "")) {
      errs.sender_domain = t("Eine eigene Absenderdomain ist in deinem Tarif nicht enthalten. Entfernen geht jederzeit.", "A custom sender domain is not included in your plan. Removing it is always possible.");
    }
    if (!/^[a-z0-9][a-z0-9._-]{0,40}$/.test(d.sender_local_part.trim().toLowerCase())) errs.sender_local_part = t("Nur Kleinbuchstaben, Ziffern, Punkt, Minus.", "Lowercase letters, digits, dot and hyphen only.");
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const domain = d.sender_domain.trim().toLowerCase();
    void run("absender", async () => {
      await api.rpc("org_mail_settings_save", { p_org: orgId, p: { ...d, sender_domain: domain } });
      // 0102: neue Domain gleich beim Versanddienst anlegen lassen (sonst übernimmt es der Zeitplan in 15 Minuten)
      if (domain && domain !== (row?.sender_domain ?? "") && !domainLocked && api.senderDomain) await api.senderDomain(orgId, "sync").catch(() => null);
    }, t("Absender und Antworten gespeichert.", "Sender and replies saved."));
  }
  return (
    <form className="panel stack" aria-labelledby="absender-title" onSubmit={submit} noValidate aria-busy={!!busy}>
      <h3 id="absender-title">{t("Absender und Antworten", "Sender and replies")}</h3>
      <p>{ta("Wohin Antworten auf Mails der Agentur gehen. Leer lassen: die Antwortadresse oben auf dieser Seite. Jede Mail sagt dem Empfänger, wohin seine Antwort geht.", "Where replies to agency emails go. Leave empty: the reply address at the top of this page. Every email tells the recipient where their reply goes.", "Wohin Antworten auf deine Mails gehen. Leer lassen: die Antwortadresse oben auf dieser Seite. Jede Mail sagt dem Empfänger, wohin seine Antwort geht.", "Where replies to your emails go. Leave empty: the reply address at the top of this page. Every email tells the recipient where their reply goes.")}</p>
      <div className="grid">
        <TextField label={t("Antworten auf Vertragsmails", "Replies to contract emails")} value={d.reply_to_contracts} onChange={set("reply_to_contracts")} error={errors.reply_to_contracts} placeholder={ta("vertraege@agentur.example", "vertraege@agentur.example", "vertraege@dein-name.example", "contracts@your-name.example")} />
        <TextField label={t("Antworten auf Rechnungs- und Mahnmails", "Replies to invoice and dunning emails")} value={d.reply_to_invoices} onChange={set("reply_to_invoices")} error={errors.reply_to_invoices} placeholder={ta("buchhaltung@agentur.example", "buchhaltung@agentur.example", "rechnungen@dein-name.example", "invoices@your-name.example")} />
        {!istCreatorKonto() && <TextField label={t("Antworten auf Mails an Creator", "Replies to emails to creators")} value={d.reply_to_creators} onChange={set("reply_to_creators")} error={errors.reply_to_creators} placeholder="talents@agentur.example" />}
      </div>
      <TextField label={t("Kopie jeder Rechnungs- und Mahnmail an", "Copy of every invoice and dunning email to")} value={d.invoice_copy_to} onChange={set("invoice_copy_to")} error={errors.invoice_copy_to} placeholder={ta("buchhaltung@agentur.example", "buchhaltung@agentur.example", "steuerberatung@example.com", "taxadviser@example.com")}
        hint={ta("Zum Beispiel die eigene Buchhaltung: jede Rechnung, Stornorechnung, Gutschrift, Zahlungserinnerung und Mahnung geht als Blindkopie auch dorthin. Leer lassen: keine Kopie. Kopie an Kontakte der Marke: Rolle „Rechnung in Kopie“ am Kontakt.", "For example your own accounting: every invoice, cancellation invoice, self-billing invoice, payment reminder and dunning notice also goes there as a blind copy. Leave empty: no copy. Copy to brand contacts: role “Invoice in copy” on the contact.", "Zum Beispiel deine Steuerberatung: jede Rechnung, Stornorechnung, Zahlungserinnerung und Mahnung geht als Blindkopie auch dorthin. Leer lassen: keine Kopie. Kopie an Kontakte der Marke: Rolle „Rechnung in Kopie“ am Kontakt.", "For example your tax adviser: every invoice, cancellation invoice, payment reminder and dunning notice also goes there as a blind copy. Leave empty: no copy. Copy to brand contacts: role “Invoice in copy” on the contact.")} />
      {/* Creator-Konto: eine Person, der Absender ist ohnehin persönlich */}
      {!istCreatorKonto() && <CheckField label={t("Persönlicher Absender", "Personal sender")} checked={d.personal_sender} onChange={set("personal_sender")}
        hint={t("Mails, die eine Person im Team auslöst (Termin, Rechnung anfordern, Korrektur …), tragen deren Antwortadresse und Signatur, sofern sie eine hinterlegt hat (Benachrichtigungen · Kanäle).", "Emails triggered by a team member (appointment, request invoice, correction …) carry their reply address and signature if they have one on file (Notifications · Channels).")} />}
      <AreaField label={ta("Signatur der Agentur", "Agency signature", "Deine Signatur", "Your signature")} value={d.signature} onChange={set("signature")} rows={3}
        hint={ta("Steht unter jeder Mail an Creator und Marken, zum Beispiel Ansprechpartner und Telefon. Klartext, Zeilenumbrüche bleiben.", "Appears below every email to creators and brands, for example contact person and phone. Plain text, line breaks are kept.", "Steht unter jeder Mail an Marken, zum Beispiel Telefon und Website. Klartext, Zeilenumbrüche bleiben.", "Appears below every email to brands, for example phone and website. Plain text, line breaks are kept.")} />
      <CheckField label={t("CreatorOffice-Hinweis in Mails anzeigen", "Show the CreatorOffice note in emails")} checked={badgeLocked ? true : badgeAn} disabled={badgeLocked || !!busy}
        onChange={(v: boolean) => badgeSetzen(v)}
        hint={ta("Am Ende eurer Mails steht ein schmales Band „Erstellt mit CreatorOffice“. Kopf, Farbe, Logo und Fußzeile bleiben eure. Ausblenden lässt es sich ab dem Tarif Agentur.", "At the end of your emails there is a narrow strip “Made with CreatorOffice”. Header, colour, logo and footer remain yours. It can be hidden from the Agency plan.", "Am Ende deiner Mails steht ein schmales Band „Erstellt mit CreatorOffice“. Kopf, Farbe, Logo und Fußzeile bleiben deine.", "At the end of your emails there is a narrow strip “Made with CreatorOffice”. Header, colour, logo and footer remain yours.")} />
      {badgeLocked && <TarifHinweis modul="module_mail_unbranded" id="badge-tarif" was={t("Mails ohne CreatorOffice-Hinweis", "Emails without the CreatorOffice note")} />}
      <details open={status !== "none" || undefined}>
        <summary>{t("Eigene Absenderdomain", "Custom sender domain")}</summary>
        <div className="stack">
          <p>{ta("Ohne eigene Domain gehen Mails über die Absenderadresse der Plattform, mit dem Namen der Agentur als Absender. Mit eigener Domain (am besten eine Subdomain wie mail.eure-agentur.de) legt CreatorOffice sie beim Versanddienst an und zeigt die DNS-Einträge; ist die Domain bestätigt, gehen Mails von der Adresse unten.", "Without a custom domain emails go out via the platform's sender address, with the agency name as sender. With your own domain (ideally a subdomain such as mail.your-agency.com) CreatorOffice creates it at the email service and shows the DNS records; once the domain is verified, emails go out from the address below.", "Ohne eigene Domain gehen Mails über die Absenderadresse der Plattform, mit deinem Namen als Absender. Mit eigener Domain (am besten eine Subdomain wie mail.dein-name.de) legt CreatorOffice sie beim Versanddienst an und zeigt die DNS-Einträge; ist die Domain bestätigt, gehen Mails von der Adresse unten.", "Without a custom domain emails go out via the platform's sender address, with your name as sender. With your own domain (ideally a subdomain such as mail.your-name.com) CreatorOffice creates it at the email service and shows the DNS records; once the domain is verified, emails go out from the address below.")}</p>
          {domainLocked && <TarifHinweis modul="module_sender_domain" id="absender-tarif" was={t("Eigene Absenderdomain", "Custom sender domain")} />}
          {/* K-385 (0289): Domain bleibt im kleineren Tarif eingerichtet, nur pausiert */}
          {domainLocked && row?.sender_domain && <p className="notice" role="status">{t(`Die Domain ${row.sender_domain} ist eingerichtet, aber pausiert: Dein Tarif enthält keine eigene Absenderdomain, Mails gehen solange über die Absenderadresse der Plattform. Mit dem Upgrade ist sie sofort wieder aktiv, ohne neue DNS-Einträge.`, `The domain ${row.sender_domain} is set up but paused: your plan does not include a custom sender domain, so emails go out via the platform's sender address. With an upgrade it is active again right away, without new DNS records.`)}</p>}
          <div className="grid">
            <TextField label={t("Adresse vor dem @", "Address before the @")} value={d.sender_local_part} onChange={set("sender_local_part")} error={errors.sender_local_part} disabled={domainLocked} />
            <TextField label="Domain" value={d.sender_domain} onChange={set("sender_domain")} error={errors.sender_domain} placeholder={ta("agentur.example", "agentur.example", "dein-name.example", "your-name.example")} disabled={domainLocked} />
          </div>
          {domainLocked && row?.sender_domain && (
            <div className="row">
              <button type="button" disabled={!!busy || !d.sender_domain} onClick={() => setD((x) => ({ ...x, sender_domain: "" }))}>{t("Eigene Domain entfernen", "Remove custom domain")}</button>
              {!d.sender_domain && <small>{t("Wird beim Speichern entfernt; danach gehen Mails über die Absenderadresse der Plattform.", "Removed when you save; afterwards emails go via the platform's sender address.")}</small>}
            </div>
          )}
          <AbsenderdomainStand orgId={orgId} domain={row?.sender_domain ?? null} localPart={row?.sender_local_part ?? "post"} status={status} ready={ready}
            records={row?.sender_domain_records ?? []} checkedAt={row?.sender_domain_checked_at ?? null} error={row?.sender_domain_error ?? null} locked={domainLocked} />
        </div>
      </details>
      <FormErrors errors={{}} error={error} />
      <div className="row"><button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}</button></div>
    </form>
  );
}

// ── Vertragsvorlage und Texte ──────────────────────────────────────────────────

interface TextEntry {
  key: string;
  label: string;
  group: string;
  /** Produkttext je Sprache; eigene Mahnstufen nutzen den Text der Erinnerung bzw. der 1. Mahnung */
  base: Record<string, PlatformTextRow | undefined>;
  fallbackFrom?: string | undefined;
}

/** Mailtext an einen Creator (Platzhalter für den Creator, mailFieldsFor) */
/** Texte, die es im Creator-Konto nicht gibt: Einladung ins Team (ein Platz), Einwilligung Erziehungsberechtigter (nur Unternehmer) */
const NUR_AGENTUR_TEXTE = ["email.invite_member", "email.guardian_consent_later"];

function anCreator(key: string): boolean {
  const felder = mailFieldsFor(key) as readonly string[];
  // Ohne eigene Liste liefert mailFieldsFor alle Platzhalter; solche Texte bleiben sichtbar
  return felder.length < MAIL_PLACEHOLDERS.length && felder.includes("talent.first_name");
}

function textEntries(d: VorlagenData): TextEntry[] {
  const byKey = new Map<string, TextEntry>();
  const groups: string[] = TEXT_GROUPS.map((g) => g.key);
  for (const t of d.platformTexts) {
    if (LEGACY_TEXT_KEYS.includes(t.key) || isHiddenText(t.key) || !groups.includes(textGroup(t.key))) continue;
    // Creator-Konto: keine Mails an Creator (Einladung, Provisionsrechnung, Auszahlung …), es gibt nur die eigene Person
    if (istCreatorKonto() && (anCreator(t.key) || NUR_AGENTUR_TEXTE.includes(t.key) || t.key.startsWith("dunning.talent."))) continue;
    const e = byKey.get(t.key) ?? { key: t.key, label: textLabel(t.key), group: textGroup(t.key), base: {} };
    e.base[t.language] = t;
    byKey.set(t.key, e);
  }
  // Eigene Mahnstufen ohne Plattformtext: der Versand nimmt den eigenen Text, sonst Erinnerung bzw. 1. Mahnung (0027)
  for (const s of d.steps) {
    const key = `dunning.${s.key}`;
    if (byKey.has(key)) continue;
    const from = s.is_reminder ? "dunning.reminder" : "dunning.first_notice";
    const base = byKey.get(from)?.base ?? {};
    byKey.set(key, { key, label: `Eigene Stufe „${stageLabel(s.key)}“`, group: "dunning", base: { ...base }, fallbackFrom: from });
  }
  return [...byKey.values()].sort((a, b) => textRank(a.key) - textRank(b.key) || a.key.localeCompare(b.key));
}

/**
 * Mailvorlagen als Liste (IA-1 Schritt 3, Zoho-Vorbild Bild 14): je Text Name, Betreff, Sprachen mit eigener Fassung und Stand;
 * „Bearbeiten“ öffnet den bisherigen Editor im Fenster (gleicher Speicherweg). Suche über Name und Betreff. Die Vertragsvorlagen
 * stehen seit Schritt 3 unter PDF-Vorlagen.
 */
function TexteForm({ orgId, orgName, currency, timeZone, data }: { orgId: string; orgName: string; currency: string; timeZone: string; data: VorlagenData }) {
  const entries = useMemo(() => textEntries(data), [data]);
  const languages = (data.settings.supported_languages ?? parsePgArray(data.defaults.supported_languages)).filter((l) => l === "de" || l === "en");
  const docLang = data.settings.default_document_language ?? data.settings.locale.slice(0, 2);
  const startLang = (languages as string[]).includes(docLang) ? docLang : languages[0] ?? "de";
  const [suche, setSuche] = useState("");
  const [offen, setOffen] = useState<string | null>(null);
  const [lang, setLang] = useState<string>(startLang);
  const eigen = (key: string, l: string) => data.orgTexts.find((x) => x.key === key && x.language === l);
  const betreff = (e: TextEntry) => {
    const own = eigen(e.key, startLang);
    const base = e.base[startLang] ?? e.base.de ?? e.base.en;
    return own?.subject ?? base?.subject ?? "";
  };
  // Betreff mit Beispielwerten wie in der Vorschau des Editors (Bedingungen und Platzhalter sind in der Liste schwer lesbar)
  const beispiel = useMemo(() => mailSampleValues(startLang === "en" ? "en" : "de", orgName, (c) => money(c, currency), todayIn(timeZone)), [startLang, orgName, currency, timeZone]);
  // Mahntexte: ohne Beispiel-Stufe, damit jede Zeile ihre eigene Stufe zeigt („1. Mahnung“ statt der Stufe des Beispiels)
  const ohneStufe = useMemo(() => Object.fromEntries(Object.entries(beispiel).filter(([k]) => k !== "dunning.stage")), [beispiel]);
  // Beispielnummer aus dem passenden Kreis der Agentur: Gutschriften und ihr Storno aus dem Gutschrift-Kreis, alles andere
  // (auch die Stornorechnung, sie läuft im Kreis ihres Belegs, 0043/0345) aus dem Rechnungskreis
  const nummer = useMemo(() => {
    const heute = todayIn(timeZone);
    const gs = (data.settings as typeof data.settings & { self_billing_number_template?: string | null }).self_billing_number_template;
    return {
      rechnung: invoiceNumberExample(data.settings.invoice_number_scheme?.template, data.settings.invoice_number_prefix, heute),
      gutschrift: invoiceNumberExample(gs || "GS-{YYYY}-{N}", "", heute),
    };
  }, [data.settings, timeZone]);
  const betreffLesbar = (e: TextEntry) => {
    const roh = betreff(e);
    if (!roh) return "";
    const werte = { ...(e.key.startsWith("dunning.") ? ohneStufe : beispiel), "invoice.number": e.key.startsWith("email.self_billing") ? nummer.gutschrift : nummer.rechnung };
    return renderPreview(roh, "", werte).subject;
  };
  const passt = (e: TextEntry) => matchesSearch(`${e.label} ${betreffLesbar(e)}`, suche);
  const entry = entries.find((e) => e.key === offen);
  const own = entry ? eigen(entry.key, lang) : undefined;
  const sprachenAuswahl = LANGUAGES.filter((l) => languages.includes(l.key));

  return (
    <section className="panel stack tk-mailvorlagen" aria-labelledby="mailvorlagen-title">
      <div className="row between">
        <h2 id="mailvorlagen-title">{t("Text für Mails, Mahnschreiben und Rechnungshinweise", "Text for emails, dunning letters and invoice notes")}</h2>
        <input type="search" className="tk-mailvorlagen-suche" value={suche} onChange={(e) => setSuche(e.target.value)}
          placeholder={t("Vorlage suchen …", "Search template …")} aria-label={t("Mailvorlagen durchsuchen", "Search email templates")} />
      </div>
      <div className="tablewrap tk-cardtable">
        <table>
          <caption className="sr-only">{t("Mailvorlagen", "Email templates")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Name", "Name")}</th>
              <th scope="col">{t("Betreff", "Subject")}</th>
              <th scope="col">{t("Eigene Fassung", "Own version")}</th>
              <th scope="col"><span className="sr-only">{t("Aktion", "Action")}</span></th>
            </tr>
          </thead>
          {TEXT_GROUPS.map((g) => {
            const zeilen = entries.filter((e) => e.group === g.key && passt(e));
            if (!zeilen.length) return null;
            return (
              <tbody key={g.key}>
                <tr className="tk-tarif-gruppe"><th scope="colgroup" colSpan={4}>{g.label}</th></tr>
                {zeilen.map((e) => {
                  const mitEigener = sprachenAuswahl.filter((l) => !!eigen(e.key, l.key));
                  return (
                    <tr key={e.key}>
                      <td data-label={t("Name", "Name")} className="tk-titel-zelle"><strong>{e.label}</strong></td>
                      <td data-label={t("Betreff", "Subject")} className="tk-mailvorlage-betreff">{betreffLesbar(e) || "–"}</td>
                      <td data-label={t("Eigene Fassung", "Own version")}>
                        {mitEigener.length
                          ? <span className="pill info"><span className="pill-zeichen">◇</span> {mitEigener.map((l) => l.label).join(", ")}</span>
                          : <span className="muted">{t("Standard", "Default")}</span>}
                      </td>
                      <td data-label={t("Aktion", "Action")}>
                        <button type="button" onClick={() => { setLang(startLang); setOffen(e.key); }}>{t("Bearbeiten", "Edit")}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            );
          })}
        </table>
      </div>
      {entries.every((e) => !passt(e)) && <p className="muted">{t("Keine Vorlage gefunden.", "No template found.")}</p>}
      <small>{t("Betreff mit Beispielwerten; in der Mail stehen die echten Werte.", "Subject with sample values; the email contains the real values.")}</small>
      <Dialog open={!!entry} onClose={() => setOffen(null)} title={entry?.label ?? ""} wide>
        {entry && (
          <div className="stack">
            {sprachenAuswahl.length > 1 && (
              <div className="row" role="group" aria-label={t("Sprache des Textes", "Language of the text")}>
                {sprachenAuswahl.map((l) => (
                  <button key={l.key} type="button" aria-pressed={lang === l.key} onClick={() => setLang(l.key)}>{l.label}</button>
                ))}
              </div>
            )}
            <TextEditor key={`${entry.key}|${lang}|${own?.updated_at ?? "standard"}`} orgId={orgId} orgName={orgName} currency={currency} timeZone={timeZone}
              entry={entry} lang={lang} own={own ? { subject: own.subject, body: own.body } : null} />
          </div>
        )}
      </Dialog>
    </section>
  );
}

const escHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function TextEditor({ orgId, orgName, currency, timeZone, entry, lang, own }: {
  orgId: string;
  orgName: string;
  currency: string;
  timeZone: string;
  entry: TextEntry;
  lang: string;
  own: { subject: string | null; body: string } | null;
}) {
  const base = entry.base[lang] ?? entry.base.de ?? entry.base.en;
  // Tarifstaffel (0099): eigene Mahntexte erst mit „Eigene Mahngebühren und Mahntexte“; zurücksetzen auf den Standard geht immer
  const dunningLocked = useModul("module_dunning_custom").gesperrt && entry.key.startsWith("dunning.");
  const [subject, setSubject] = useState(own?.subject ?? base?.subject ?? "");
  const [body, setBody] = useState(own?.body ?? base?.body ?? "");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmReset, setConfirmReset] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const { busy, error, run } = useAction();
  const today = todayIn(timeZone);
  // Beispielwerte für jeden Platzhalter (Befund 19): ein unveränderter Standardtext meldet nie „offene Platzhalter“
  const preview = renderPreview(subject, body, mailSampleValues(lang === "en" ? "en" : "de", orgName, (c) => money(c, currency), today));
  const dropped = base ? droppedPlaceholders({ subject: base.subject, body: base.body }, { subject, body }) : [];
  // Werte, die diese Mail nie hat: mit ihnen nimmt der Versand den Standardtext
  const fremd = isMail(entry.key) ? unsupportedPlaceholders(entry.key, subject, body) : [];
  const angebot = isMail(entry.key) ? mailFieldsFor(entry.key).filter((p) => (MAIL_PLACEHOLDERS as readonly string[]).includes(p)) : [...MAIL_PLACEHOLDERS];
  const isHtml = /<\w+/.test(body);
  const srcDoc = `<!doctype html><html lang="${lang}"><head><meta charset="utf-8"><style>body{font:15px/1.55 Arial,sans-serif;color:#202724;background:#FFFFFF;margin:16px}small{color:#57655E}</style></head><body><small>Betreff: ${escHtml(preview.subject)}</small><hr>${isHtml ? preview.html : `<p>${preview.html}</p>`}</body></html>`;

  function insert(p: string) {
    const token = `{{${p}}}`;
    const el = area.current;
    if (!el) return setBody((b) => b + token);
    const start = el.selectionStart ?? body.length, end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (dunningLocked) return;
    const errs: Record<string, string> = {};
    if (!body.trim()) errs.body = t("Bitte einen Text eingeben.", "Please enter a text.");
    if (base?.subject && !subject.trim()) errs.subject = t("Bitte einen Betreff eingeben.", "Please enter a subject.");
    const unknown = unknownPlaceholders(subject, body);
    if (unknown.length) errs.body = t(`Diese Platzhalter gibt es nicht: ${unknown.map((u) => `{{${u}}}`).join(", ")}.`, `These placeholders do not exist: ${unknown.map((u) => `{{${u}}}`).join(", ")}.`);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    void run("text", () => saveOrgText(api, orgId, entry.key, lang, subject.trim() || null, body), t("Eigene Fassung gespeichert.", "Own version saved."));
  }

  return (
    <>
      <form className="stack" onSubmit={submit} noValidate aria-busy={!!busy}>
        <p>
          {own ? <span className="pill info"><span className="pill-zeichen">◇</span> {t("Eigene Fassung", "Own version")}</span> : <span className="pill"><span className="pill-zeichen">○</span> {t("Standard der Plattform", "Platform default")}</span>}{" "}
          {entry.fallbackFrom && !own ? t(`Ohne eigenen Text nutzt der Versand „${textLabel(entry.fallbackFrom)}“.`, `Without its own text, sending uses “${textLabel(entry.fallbackFrom)}”.`) : ""}
        </p>
        {dunningLocked && <TarifHinweis modul="module_dunning_custom" id="mahntext-tarif" was={t("Eigene Mahntexte", "Custom dunning texts")} />}
        <TextField label={t("Betreff", "Subject")} value={subject} onChange={setSubject} error={errors.subject} disabled={dunningLocked} />
        <AreaField label="Text" value={body} onChange={setBody} error={errors.body} rows={10} textareaRef={area} disabled={dunningLocked}
          hint={t("Einfaches HTML ist erlaubt (Absätze, Zeilenumbrüche, Links). Werte setzt der Versand über die Platzhalter ein.", "Simple HTML is allowed (paragraphs, line breaks, links). Sending inserts values via the placeholders.")} />
        <div className="stack">
          <small>{t("Platzhalter einfügen", "Insert placeholder")}{isMail(entry.key) ? t(" (nur Werte, die diese Mail wirklich hat)", " (only values this email really has)") : ""}</small>
          <div className="tk-placeholders">
            {angebot.map((p) => <button key={p} type="button" disabled={dunningLocked} onClick={() => insert(p)}>{`{{${p}}}`}</button>)}
          </div>
        </div>
        {fremd.length > 0 && (
          <div className="notice">{t("Diese Mail hat nie einen Wert für ", "This email never has a value for ")}{fremd.map((p) => `{{${p}}}`).join(", ")}{t(". Mit diesen Platzhaltern nimmt der Versand für jede Mail den Standardtext; nimm sie heraus oder setze sie in einen bedingten Teil (", ". With these placeholders, sending uses the default text for every email; remove them or put them in a conditional part (")}{"{{#…}} … {{/…}}"}).</div>
        )}
        {dropped.length > 0 && (
          <div className="notice">{t("Der Standardtext nutzt ", "The default text uses ")}{dropped.map((p) => `{{${p}}}`).join(", ")}{t("; deine Fassung nicht. Prüfe, ob die Angabe fehlt.", "; your version does not. Check whether the detail is missing.")}</div>
        )}
        {preview.unresolved.length > 0 && !errors.body && (
          <small className="tk-error">{t("Offene Platzhalter: ", "Unresolved placeholders: ")}{preview.unresolved.map((u) => `{{${u}}}`).join(", ")}{t(". Mit offenen Platzhaltern geht keine Mail raus.", ". No email goes out with unresolved placeholders.")}</small>
        )}
        <iframe className="tk-preview" sandbox="" title={t("Vorschau mit Beispielwerten", "Preview with sample values")} srcDoc={srcDoc} />
        <small>{t("Vorschau mit Beispielwerten. Die echte Mail enthält die Werte der jeweiligen Rechnung, des Vertrags oder der Einladung.", "Preview with sample values. The real email contains the values of the respective invoice, contract or invitation.")}</small>
        <FormErrors errors={{}} error={error} />
        <div className="row">
          <button className="primary" type="submit" disabled={!!busy || dunningLocked} aria-describedby={dunningLocked ? "mahntext-tarif" : undefined}>{busy === "text" ? t("Wird gespeichert …", "Saving …") : t("Änderungen speichern", "Save changes")}</button>
          <button className="" type="button" disabled={!own || !!busy} onClick={() => setConfirmReset(true)}>{t("Auf Standard zurücksetzen", "Reset to default")}</button>
        </div>
      </form>
      <Confirm open={confirmReset} title={t("Eigene Fassung löschen?", "Delete own version?")} busy={busy === "reset"} confirmLabel={t("Zurücksetzen", "Reset")} danger
        text={t("Danach gilt wieder der Text der Plattform. Die eigene Fassung lässt sich nicht wiederherstellen.", "Afterwards the platform's text applies again. Your own version cannot be restored.")}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => void run("reset", () => resetOrgText(api, orgId, entry.key, lang), t("Standardtext gilt wieder.", "Default text applies again.")).then(() => setConfirmReset(false))} />
    </>
  );
}

// ── Artikelkatalog ─────────────────────────────────────────────────────────────

/** IA-1: Artikelkatalog auf der Seite Positionsarten & Artikel (gleiche Daten und Abfrage wie die Vorlagen) */
export function ArtikelBereich({ orgId, currency }: { orgId: string; currency: string }) {
  const q = useQuery({ queryKey: ["org_texts", orgId, "vorlagen"], queryFn: () => loadVorlagen(api, orgId) });
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  return <ArtikelPanel orgId={orgId} currency={currency} articles={q.data.articles} />;
}

function ArtikelPanel({ orgId, currency, articles }: { orgId: string; currency: string; articles: ArticleDbRow[] }) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"alle" | "aktiv" | "inaktiv">("aktiv");
  const [edit, setEdit] = useState<ArticleDbRow | "neu" | null>(null);
  const [remove, setRemove] = useState<ArticleDbRow | null>(null);
  const { busy, run } = useAction();
  const shown = articles.filter((a) => (filter === "alle" || (filter === "aktiv" ? a.active : !a.active))
    && matchesSearch(`${a.name} ${a.description ?? ""} ${a.category ?? ""}`, search));
  return (
    <section className="panel stack" aria-labelledby="artikel-title">
      <div className="row between">
        <h3 id="artikel-title">{t("Artikelkatalog", "Item catalogue")}</h3>
        <button className="primary" type="button" onClick={() => setEdit("neu")}>{t("Artikel anlegen", "Create item")}</button>
      </div>
      <p>{ta("Leistungen mit Standardpreis für den Rechnungsgenerator. Creator sehen nur aktive Artikel.", "Services with a default price for the invoice generator. Creators only see active items.", "Leistungen mit Standardpreis für den Rechnungsgenerator.", "Services with a default price for the invoice generator.")}</p>
      <Toolbar search={search} onSearch={setSearch} label={t("Artikel suchen", "Search items")} placeholder={t("Artikel, Kategorie …", "Item, category …")}
        filters={[{ key: "aktiv", label: t("Aktiv", "Active"), count: articles.filter((a) => a.active).length }, { key: "inaktiv", label: t("Inaktiv", "Inactive") }, { key: "alle", label: t("Alle", "All") }]}
        filter={filter} onFilter={setFilter} />
      <div className="tablewrap">
        <table>
          <caption className="sr-only">{t("Artikelkatalog für Rechnungen", "Item catalogue for invoices")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Artikel", "Item")}</th>
              <th scope="col">{t("Kategorie", "Category")}</th>
              <th scope="col">{t("Einheit", "Unit")}</th>
              <th scope="col" className="money">{t("Standardpreis", "Default price")}</th>
              {!istCreatorKonto() && <th scope="col">{t("Provision", "Commission")}</th>}
              <th scope="col">Status</th>
              <th scope="col">{t("Aktion", "Action")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((a) => (
              <tr key={a.id} className={a.active ? undefined : "tk-muted-row"}>
                <td><strong>{a.name}</strong>{a.description && <small>{a.description}</small>}</td>
                <td>{a.category ?? "–"}</td>
                <td>{a.unit ?? "–"}</td>
                <td>{a.default_price_cents === null ? "–" : <Money cents={a.default_price_cents} currency={a.currency ?? currency} />}</td>
                {!istCreatorKonto() && <td>{a.commissionable ? <span className="pill"><span className="pill-zeichen">○</span> {t("Mit Provision", "With commission")}</span> : <span className="pill info"><span className="pill-zeichen">◇</span> {t("Provisionsfrei", "Commission-free")}</span>}</td>}
                <td>{a.active ? <span className="pill success"><span className="pill-zeichen">✓</span> {t("Aktiv", "Active")}</span> : <span className="pill"><span className="pill-zeichen">○</span> {t("Inaktiv", "Inactive")}</span>}</td>
                <td>
                  <div className="row">
                    <button type="button" onClick={() => setEdit(a)}>{t("Bearbeiten", "Edit")}</button>
                    <button type="button" className="danger" onClick={() => setRemove(a)}>{t("Löschen", "delete")}</button>
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td colSpan={istCreatorKonto() ? 6 : 7}>{search ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.") : t("Noch keine Artikel. Lege Leistungen an, die in Rechnungen immer wiederkehren.", "No items yet. Create services that recur in invoices.")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Dialog open={edit !== null} onClose={() => setEdit(null)} title={edit === "neu" ? t("Artikel anlegen", "Create item") : t("Artikel bearbeiten", "Edit item")} wide>
        {edit !== null && <ArtikelForm key={edit === "neu" ? "neu" : edit.id} orgId={orgId} currency={currency} article={edit === "neu" ? null : edit} onDone={() => setEdit(null)} />}
      </Dialog>
      <Confirm open={!!remove} title={t("Artikel löschen?", "Delete item?")} danger busy={busy === "remove"} confirmLabel={t("Löschen", "delete")}
        text={remove ? t(`„${remove.name}“ wird gelöscht. Rechnungen, die ihn schon nutzen, behalten ihre Positionen. Soll er nur nicht mehr angeboten werden, reicht „inaktiv“.`, `“${remove.name}” will be deleted. Invoices already using it keep their items. If it should just no longer be offered, “inactive” is enough.`) : ""}
        onClose={() => setRemove(null)} onConfirm={() => void run("remove", () => deleteArticle(api, remove!.id), t("Artikel gelöscht.", "Item deleted.")).then(() => setRemove(null))} />
    </section>
  );
}

function ArtikelForm({ orgId, currency, article, onDone }: { orgId: string; currency: string; article: ArticleDbRow | null; onDone: () => void }) {
  const [d, setD] = useState<ArticleDraft>(article ? {
    name: article.name, description: article.description ?? "", category: article.category ?? "", price: centsToText(article.default_price_cents),
    currency: article.currency ?? currency, unit: article.unit ?? "", commissionable: article.commissionable, active: article.active, sort_order: String(article.sort_order),
  } : emptyArticle(currency));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { busy, error, run } = useAction();
  const set = <K extends keyof ArticleDraft>(k: K) => (v: ArticleDraft[K]) => setD((x) => ({ ...x, [k]: v }));
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = validateArticle(d);
    setErrors(r.errors);
    if (!r.row) return;
    void run("save", () => saveArticle(api, orgId, article?.id ?? null, r.row!), article ? t("Artikel gespeichert.", "Item saved.") : t("Artikel angelegt.", "Item created.")).then((x) => x && onDone());
  }
  return (
    <form className="stack" onSubmit={submit} noValidate aria-busy={!!busy}>
      <TextField label={t("Bezeichnung", "Name")} value={d.name} onChange={set("name")} error={errors.name} />
      <AreaField label={t("Beschreibung", "Description")} value={d.description} onChange={set("description")} error={errors.description} rows={3} />
      <div className="grid">
        <TextField label={t("Kategorie", "Category")} value={d.category} onChange={(v) => setD((x) => ({ ...x, category: v, commissionable: v === "Reise/Auslagen" ? false : x.commissionable }))}
          error={errors.category} list="tk-kategorien" />
        <SelectField label={t("Einheit", "Unit")} value={d.unit} onChange={set("unit")} options={ARTICLE_UNITS.map((u) => ({ key: u, label: u }))} placeholder={t("Ohne Einheit", "No unit")} />
      </div>
      <datalist id="tk-kategorien">{ARTICLE_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
      <div className="grid">
        <TextField label={t("Standardpreis netto", "Default price net")} value={d.price} onChange={set("price")} error={errors.price} inputMode="decimal" placeholder="optional" hint={betragHinweis(d.price, d.currency)?.text} />
        <SelectField label={t("Währung", "Currency")} value={d.currency} onChange={set("currency")} options={CURRENCIES.map((c) => ({ key: c, label: c }))} error={errors.currency} />
      </div>
      {!istCreatorKonto() && <CheckField label={t("Provision fällt an", "Commission applies")} checked={d.commissionable} onChange={set("commissionable")} hint={t("Aus für durchlaufende Kosten wie Reise, Auslagen oder Produkte.", "Off for pass-through costs such as travel, expenses or products.")} />}
      <CheckField label={t("Aktiv (im Rechnungsgenerator wählbar)", "Active (selectable in the invoice generator)")} checked={d.active} onChange={set("active")} />
      <TextField label={t("Reihenfolge", "Order")} value={d.sort_order} onChange={set("sort_order")} error={errors.sort_order} inputMode="numeric" hint={t("Kleinere Zahl steht weiter oben.", "A smaller number appears higher up.")} />
      <FormErrors errors={errors} error={error} />
      <div className="row">
        <button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : article ? t("Änderungen speichern", "Save changes") : t("Artikel anlegen", "Create item")}</button>
        <button type="button" onClick={onDone}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </form>
  );
}

// ── Status und Begriffe ────────────────────────────────────────────────────────

/** Nur die Status von Kooperationen: nur deren deutsche Bezeichnung erscheint bisher in der Oberfläche (Kooperationen). */
const DEAL_STATUS_KEYS = STATUS_KEYS.filter((s) => s.group === "Kooperation");

function BegriffePanel({ orgId, labels }: { orgId: string; labels: StatusLabelRow[] }) {
  const initial = Object.fromEntries(DEAL_STATUS_KEYS.map((s) => {
    const l = labels.find((x) => x.status === s.status);
    return [s.status, { label_de: l?.label_de ?? "", label_en: l?.label_en ?? "", hidden: l?.hidden ?? false }];
  }));
  const [rows, setRows] = useState(initial);
  const { busy, error, run } = useAction();
  const changed = DEAL_STATUS_KEYS.filter((s) => JSON.stringify(rows[s.status]) !== JSON.stringify(initial[s.status]));
  function save() {
    void run("labels", async () => {
      for (const s of changed) {
        const r = rows[s.status]!;
        await saveStatusLabel(api, orgId, s.status, { label_de: r.label_de.trim() || null, label_en: r.label_en.trim() || null, hidden: r.hidden });
      }
    }, changed.length === 1 ? t("Bezeichnung gespeichert.", "Label saved.") : t(`${changed.length} Bezeichnungen gespeichert.`, `${changed.length} labels saved.`));
  }
  return (
    <section className="panel stack" aria-labelledby="begriffe-title">
      <details>
        <summary id="begriffe-title">{t("Status und Begriffe", "Statuses and terms")}</summary>
        <div className="stack">
          <p>{t("Eigene Bezeichnungen für die Status von Kooperationen; sie erscheinen in Liste, Tafel und Detail der Kooperationen. Die Abfolge bleibt fest. Leer lassen: Bezeichnung der Plattform. Status von Monaten und Rechnungen haben feste Bezeichnungen.", "Custom labels for deal statuses; they appear in the list, board and detail of deals. The order stays fixed. Leave empty: platform label. Statuses of months and invoices have fixed labels.")}</p>
          <div className="tablewrap tk-cardtable">
            <table>
              <caption className="sr-only">{t("Statusbezeichnungen", "Status labels")}</caption>
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">{t("Eigene Bezeichnung", "Custom label")}</th>
                  <th scope="col">{t("In Auswahllisten", "In selection lists")}</th>
                </tr>
              </thead>
              <tbody>
                {DEAL_STATUS_KEYS.map((s) => {
                  const r = rows[s.status]!;
                  const set = (patch: Partial<typeof r>) => setRows((x) => ({ ...x, [s.status]: { ...r, ...patch } }));
                  return (
                    <tr key={s.status}>
                      <td data-label="Status">{s.label}<small className="mono">{s.status}</small></td>
                      <td data-label={t("Eigene Bezeichnung", "Custom label")}><input className="tk-table-input" aria-label={t(`Eigene Bezeichnung für ${s.label}`, `Custom label for ${s.label}`)} value={r.label_de} placeholder={s.label} onChange={(e) => set({ label_de: e.target.value })} /></td>
                      <td data-label={t("In Auswahllisten", "In selection lists")}><label className="row"><input type="checkbox" checked={!r.hidden} aria-label={t(`${s.label} in Auswahllisten zeigen`, `Show ${s.label} in selection lists`)} onChange={(e) => set({ hidden: !e.target.checked })} /><span>{r.hidden ? t("Aus", "Off") : t("An", "On")}</span></label></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <FormErrors errors={{}} error={error} />
          <div className="row">
            <button className="primary" type="button" disabled={!changed.length || !!busy} onClick={save}>{busy ? t("Wird gespeichert …", "Saving …") : t("Begriffe speichern", "Save terms")}</button>
          </div>
        </div>
      </details>
    </section>
  );
}
